'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import { Customer } from './customerTypes';

import { Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (c: Customer) => void;
  nextNumber: number;
}

interface FormData {
  fullName: string;
  email: string;
  mobile: string;
  address: string;
  landmark: string;
  machineModel: string;
  monthlyFee: number;
  serviceStartDate: string;
  assignedTechnicianId: string;
  notes: string;
}

interface TechOption {
  id: string;
  full_name: string;
}

export default function AddCustomerModal({ open, onClose, onAdd, nextNumber }: Props) {
  const [loading, setLoading] = useState(false);
  const [technicians, setTechnicians] = useState<TechOption[]>([]);
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const customerNumber = `CUST-2026-${String(nextNumber).padStart(3, '0')}`;

  useEffect(() => {
    if (open) {
      supabase
        .from('technicians')
        .select('id, full_name')
        .eq('status', 'active')
        .then(({ data }) => setTechnicians(data || []));
    }
  }, [open]);

  function onSubmit(data: FormData) {
    setLoading(true);
    const selectedTech = technicians.find((t) => t.id === data.assignedTechnicianId);
    const newCustomer: Customer = {
      id: `temp-${Date.now()}`,
      customerNumber,
      fullName: data.fullName,
      email: data.email,
      mobile: data.mobile,
      address: data.address,
      landmark: data.landmark,
      machineModel: data.machineModel,
      monthlyFee: Number(data.monthlyFee),
      serviceStartDate: data.serviceStartDate,
      monthsActive: 0,
      contractStatus: 'active',
      lockInMonths: 0,
      assignedTechnician: selectedTech?.full_name || '',
      notes: data.notes,
    };
    onAdd(newCustomer);
    reset();
    setLoading(false);
  }

  return (
    <Modal open={open} onClose={onClose} title="Register New Customer" size="lg">
      <div className="mb-4 bg-secondary rounded-lg px-4 py-3 flex items-center gap-3">
        <div>
          <p className="text-xs text-muted-foreground font-600 uppercase tracking-wide">Auto-assigned Customer Number</p>
          <p className="font-mono text-lg font-700 text-primary">{customerNumber}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Full Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              {...register('fullName', { required: 'Full name is required' })}
              placeholder="e.g. Maria Santos Reyes"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.fullName && <p className="mt-1 text-xs text-danger">{errors.fullName.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Email Address <span className="text-danger">*</span>
            </label>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
              placeholder="customer@email.com"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Mobile Number <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-1">Philippine mobile number (09XX-XXX-XXXX)</p>
            <input
              type="tel"
              {...register('mobile', { required: 'Mobile number is required' })}
              placeholder="09171234567"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.mobile && <p className="mt-1 text-xs text-danger">{errors.mobile.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Machine Type / Model <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              {...register('machineModel', { required: 'Machine model is required' })}
              placeholder="e.g. Leenistech RO-5S, Alkaline Pro"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.machineModel && <p className="mt-1 text-xs text-danger">{errors.machineModel.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Monthly Service Fee (₱) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              {...register('monthlyFee', { required: 'Monthly fee is required', min: { value: 1, message: 'Must be greater than 0' } })}
              placeholder="e.g. 350"
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.monthlyFee && <p className="mt-1 text-xs text-danger">{errors.monthlyFee.message}</p>}
          </div>

          <div>
            <label className="block text-xs font-600 text-foreground mb-1">
              Service Start Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              {...register('serviceStartDate', { required: 'Start date is required' })}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.serviceStartDate && <p className="mt-1 text-xs text-danger">{errors.serviceStartDate.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1">
            Complete Address <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            {...register('address', { required: 'Address is required' })}
            placeholder="House No., Street, Barangay, City, Province"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.address && <p className="mt-1 text-xs text-danger">{errors.address.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1">Landmark</label>
          <p className="text-xs text-muted-foreground mb-1">Helps technicians locate the property quickly</p>
          <input
            type="text"
            {...register('landmark')}
            placeholder="e.g. Near SM Supermarket, Blue gate"
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1">Assigned Technician</label>
          <select
            {...register('assignedTechnicianId')}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">— Unassigned —</option>
            {technicians.map((t) => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1">Notes</label>
          <textarea
            {...register('notes')}
            rows={3}
            placeholder="Additional notes about machine location, access instructions, special requests..."
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-600 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 text-sm font-600 btn-primary rounded-lg flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Registering...
              </>
            ) : (
              `Register as ${customerNumber}`
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}